import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    IconButton,
    Link,
    Chip,
    Tooltip,
    Avatar,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import {
    KeyboardArrowRightRounded,
    AddRounded,
    Psychology,
    FactCheckOutlined,
    FastfoodOutlined,
    HomeOutlined,
    PersonOutline,
    RestaurantMenuOutlined,
    KeyboardArrowLeftRounded,
} from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GlassContainer from "../components/liquid-glass";
import { useDispatch, useSelector } from "react-redux";
import { toggleDarkMode } from "../../../redux/slices/partnersSlice";
import { AppDispatch, RootState } from "../../../redux/store";
import { User } from "../../../models/types";

/* ────────────────────────────────────────────────────────────
   Types
   ──────────────────────────────────────────────────────────── */

type ContentKey = "overview" | "clients" | "vault";
type VaultSection = "exercises" | "templates" | "recipes" | "mealPlans";

interface NavItem {
    id: string;
    label: string;
    icon: React.ElementType;
    content: ContentKey | null;
    comingSoon?: boolean;
    highlight?: boolean;
    vaultSection?: VaultSection;
    count?: number;
    shortcut?: string;
}

interface NavSection {
    title: string;
    items: NavItem[];
}

export interface PartnerSidebarProps {
    activeContent: ContentKey;
    activeTitle: string;
    isSidebarCollapsed: boolean;
    setIsSidebarCollapsed: (v: boolean | ((prev: boolean) => boolean)) => void;
    onNavClick: (
        label: string,
        content: ContentKey | null,
        comingSoon?: boolean,
        vaultSection?: VaultSection | null
    ) => void;
    onAddClient: () => void;
    onOpenQuiz: () => void;
    partnerLoading: boolean;
    hasToken: boolean;
    activeVaultSection: VaultSection | null;
}

/* ────────────────────────────────────────────────────────────
   Constants
   ──────────────────────────────────────────────────────────── */

const COLLAPSED_W = 68;
const EXPANDED_W = 260;

/* Accent colours per item id */
const ACCENT: Record<string, string> = {
    overview: "#FFD54F",
    clients: "#4FC3F7",
    quiz: "#66BB6A",
    "vault-exercises-1": "#FFB74D",
    "vault-workouts": "#9575CD",
    "vault-recipes": "#F06292",
    "vault-mealplans": "#7986CB",
};

/* Gradient pairs for the active bar */
const GRADIENT: Record<string, [string, string]> = {
    overview: ["#FFD54F", "#FFB300"],
    clients: ["#4FC3F7", "#0288D1"],
    quiz: ["#66BB6A", "#2E7D32"],
    "vault-exercises-1": ["#FFB74D", "#F57C00"],
    "vault-workouts": ["#9575CD", "#5E35B1"],
    "vault-recipes": ["#F06292", "#C2185B"],
    "vault-mealplans": ["#7986CB", "#3949AB"],
};

/* ────────────────────────────────────────────────────────────
   Keyframes (injected once)
   ──────────────────────────────────────────────────────────── */
const keyframesInjected = { current: false };
function injectKeyframes() {
    if (keyframesInjected.current) return;
    keyframesInjected.current = true;
    const style = document.createElement("style");
    style.textContent = `
    @keyframes sidebarShimmer {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes sidebarPulseRing {
      0%   { transform: scale(1); opacity: 0.6; }
      70%  { transform: scale(1.25); opacity: 0; }
      100% { transform: scale(1.25); opacity: 0; }
    }
    @keyframes sidebarGlowDot {
      0%, 100% { opacity: 1; }
      50%      { opacity: 0.4; }
    }
    @keyframes sidebarFadeUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes sidebarIconBounce {
      0%   { transform: translateY(0); }
      40%  { transform: translateY(-3px); }
      60%  { transform: translateY(-1px); }
      100% { transform: translateY(0); }
    }
    @keyframes sidebarGradientShift {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%;}
      100% { background-position: 0% 50%; }
    }
  `;
    document.head.appendChild(style);
}

/* ────────────────────────────────────────────────────────────
   Component
   ──────────────────────────────────────────────────────────── */

const PartnerSidebar: React.FC<PartnerSidebarProps> = ({
    activeContent,
    activeTitle,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    onNavClick,
    onAddClient,
    onOpenQuiz,
    partnerLoading,
    hasToken,
    activeVaultSection,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const darkMode = useSelector((state: RootState) => state.partners.darkMode);

    const partner: User | null = useSelector(
        (state: RootState) =>
            state.partners?.partnerData?.partnerData as User | null
    );

    const exercisesCount = partner?.customExercises?.length ?? 0;
    const templatesCount = partner?.templates?.length ?? 0;
    const recipesCount =
        (
            (useSelector(
                (state: RootState) => (state.partners as any).recipes
            ) as any[]) || []
        ).length ?? 0;
    const mealPlansCount =
        (
            (useSelector(
                (state: RootState) => (state.partners as any).mealPlans
            ) as any[]) || []
        ).length ?? 0;

    // Mount animation
    const [visible, setVisible] = useState(false);
    const [showShortcuts, setShowShortcuts] = useState(false);

    useEffect(() => {
        injectKeyframes();
        const t = setTimeout(() => setVisible(true), 50);
        return () => clearTimeout(t);
    }, []);

    /* Nav data */
    const navSections: NavSection[] = [
        {
            title: "Main",
            items: [
                {
                    id: "overview",
                    label: "Overview",
                    icon: HomeOutlined,
                    content: "overview",
                    shortcut: "⌘1",
                },
                {
                    id: "clients",
                    label: "Clients",
                    icon: PersonOutline,
                    content: "clients",
                    highlight: true,
                    shortcut: "⌘2",
                },
                {
                    id: "quiz",
                    label: "Client Quiz",
                    icon: Psychology,
                    content: null,
                    shortcut: "⌘3",
                },
            ],
        },
        {
            title: "Exercise Vault",
            items: [
                {
                    id: "vault-exercises-1",
                    label: "Exercises",
                    icon: FitnessCenterIcon,
                    content: "vault",
                    vaultSection: "exercises",
                    count: exercisesCount,
                    shortcut: "⌘4",
                },
                {
                    id: "vault-workouts",
                    label: "Exercise Templates",
                    icon: FactCheckOutlined,
                    content: "vault",
                    vaultSection: "templates",
                    count: templatesCount,
                    shortcut: "⌘5",
                },
            ],
        },
        {
            title: "Food Vault",
            items: [
                {
                    id: "vault-recipes",
                    label: "Recipes",
                    icon: RestaurantMenuOutlined,
                    content: "vault",
                    vaultSection: "recipes",
                    count: recipesCount,
                    shortcut: "⌘6",
                },
                {
                    id: "vault-mealplans",
                    label: "Recipe Plans",
                    icon: FastfoodOutlined,
                    content: "vault",
                    vaultSection: "mealPlans",
                    count: mealPlansCount,
                    shortcut: "⌘7",
                },
            ],
        },
    ];

    const isItemActive = (item: NavItem) => {
        if (item.comingSoon) return false;
        if (item.content === "vault") {
            return (
                activeContent === "vault" && activeVaultSection === item.vaultSection
            );
        }
        return item.content === activeContent && item.label === activeTitle;
    };

    const collapsed = isSidebarCollapsed;

    /* ──────────── Render ──────────── */

    return (
        <Box
            onMouseEnter={() => setShowShortcuts(true)}
            onMouseLeave={() => setShowShortcuts(false)}
            sx={{
                width: collapsed ? COLLAPSED_W : EXPANDED_W,
                transition: "width 280ms cubic-bezier(.4,0,.2,1)",
                display: "flex",
                flexDirection: "column",
                zIndex: 5,
                p: 1,
                boxSizing: "border-box",
                height: "100vh",
                animation: "sidebarFadeUp 0.5s ease forwards",
            }}
        >
            <GlassContainer
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 4,
                    overflow: "hidden",
                }}
                dontHoverPop
                isDark={darkMode}
            >
                {/* ═══════ HEADER ═══════ */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: collapsed ? "center" : "space-between",
                        px: collapsed ? 0.5 : 1.5,
                        py: 1.5,
                        gap: 1,
                        borderBottom: `1px solid ${darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"
                            }`,
                    }}
                >
                    {/* Logo */}
                    {!collapsed && <IconButton
                        component={Link}
                        href="/"
                        sx={{
                            p: 0.5,
                            transition: "transform 0.25s ease",
                            "&:hover": { transform: "scale(1.08)" },
                        }}
                    >
                        <Box
                            component="img"
                            src="/icon192.png"
                            alt="Logo"
                            sx={{
                                display: "block",
                                width: collapsed ? 28 : 34,
                                height: collapsed ? 28 : 34,
                                borderRadius: 2,
                                transition: "width 0.25s ease, height 0.25s ease",
                            }}
                        />
                    </IconButton>}

                    {!collapsed && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            {/* Dark mode toggle */}
                            <GlassContainer isDark={darkMode} isMetallic={false}>
                                <Box
                                    onClick={() => dispatch(toggleDarkMode())}
                                    sx={{
                                        width: 64,
                                        height: 30,
                                        borderRadius: 20,
                                        padding: "2px",
                                        cursor: "pointer",
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        transition: "background-color .3s ease",
                                    }}
                                >
                                    {/* Sun */}
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            opacity: darkMode ? 0.35 : 1,
                                            transition: "opacity .3s ease, transform .3s ease",
                                            transform: darkMode ? "rotate(-30deg)" : "rotate(0deg)",
                                            ml: "5px",
                                        }}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={darkMode ? "#666" : "#FA8C16"}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="5" />
                                            <line x1="12" y1="1" x2="12" y2="3" />
                                            <line x1="12" y1="21" x2="12" y2="23" />
                                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                                            <line x1="1" y1="12" x2="3" y2="12" />
                                            <line x1="21" y1="12" x2="23" y2="12" />
                                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                                        </svg>
                                    </Box>

                                    {/* Moon */}
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            opacity: darkMode ? 1 : 0.35,
                                            transition: "opacity .3s ease, transform .3s ease",
                                            transform: darkMode ? "rotate(0deg)" : "rotate(30deg)",
                                            mr: "5px",
                                        }}
                                    >
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={darkMode ? "#C4B5FD" : "#999"}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                                        </svg>
                                    </Box>

                                    {/* Knob */}
                                    <Box
                                        sx={{
                                            width: 26,
                                            height: 26,
                                            position: "absolute",
                                            top: "2px",
                                            left: !darkMode ? "36px" : "2px",
                                            borderRadius: "50%",
                                            backgroundColor: darkMode ? "#2D2B55" : "#ffffff",
                                            boxShadow: darkMode
                                                ? "0 0 10px rgba(196,181,253,0.3), 0 4px 12px rgba(0,0,0,.4)"
                                                : "0 4px 12px rgba(0,0,0,.2)",
                                            transition:
                                                "left .3s cubic-bezier(.4,0,.2,1), background-color .3s ease, box-shadow .3s ease",
                                        }}
                                    />
                                </Box>
                            </GlassContainer>

                            {/* Collapse toggle */}
                            <Tooltip title="Collapse sidebar" placement="bottom">
                                <IconButton
                                    size="small"
                                    onClick={() => setIsSidebarCollapsed((prev: boolean) => !prev)}
                                    sx={{
                                        transition: "transform 0.2s ease, background-color 0.2s ease",
                                        "&:hover": {
                                            backgroundColor: darkMode
                                                ? "rgba(255,255,255,0.08)"
                                                : "rgba(0,0,0,0.06)",
                                            transform: "scale(1.1)",
                                        },
                                    }}
                                >
                                    <KeyboardArrowLeftRounded
                                        fontSize="small"
                                        sx={{
                                            color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {collapsed && (
                        <Tooltip title="Expand sidebar" placement="right">
                            <IconButton
                                size="small"
                                onClick={() => setIsSidebarCollapsed(false)}
                                sx={{
                                    transition: "transform 0.2s ease",
                                    "&:hover": {
                                        transform: "scale(1.1)",
                                        backgroundColor: darkMode
                                            ? "rgba(255,255,255,0.08)"
                                            : "rgba(0,0,0,0.06)",
                                    },
                                }}
                            >
                                <KeyboardArrowRightRounded
                                    fontSize="small"
                                    sx={{
                                        color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* ═══════ PROFILE SECTION ═══════ */}
                {!collapsed && (
                    <Box
                        sx={{
                            px: 2,
                            py: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            opacity: visible ? 1 : 0,
                            transform: visible ? "translateY(0)" : "translateY(8px)",
                            transition: "opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s",
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    fontWeight: 600,
                                    color: darkMode ? "#E8E8ED" : "#1D1D1F",
                                    fontSize: "0.85rem",
                                    lineHeight: 1.3,
                                }}
                            >
                                {partner?.name || "Partner"}
                            </Typography>
                            {/* PRO Badge */}
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.4,
                                    mt: 0.3,
                                    px: 0.8,
                                    py: 0.15,
                                    borderRadius: 1,
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: darkMode ? "#FFD54F" : "#F57C00",
                                    background: darkMode
                                        ? "linear-gradient(135deg, rgba(255,213,79,0.12) 0%, rgba(255,183,77,0.08) 100%)"
                                        : "linear-gradient(135deg, rgba(255,213,79,0.2) 0%, rgba(255,183,77,0.12) 100%)",
                                    border: `1px solid ${darkMode
                                        ? "rgba(255,213,79,0.2)"
                                        : "rgba(245,124,0,0.15)"
                                        }`,
                                    backgroundSize: "200% 100%",
                                    animation: "sidebarGradientShift 3s ease infinite",
                                }}
                            >
                                ★ PRO Trainer
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* ═══════ NAV SECTIONS ═══════ */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        py: 0.5,
                        "&::-webkit-scrollbar": { width: 4 },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: darkMode
                                ? "rgba(255,255,255,0.1)"
                                : "rgba(0,0,0,0.1)",
                            borderRadius: 2,
                        },
                    }}
                >
                    {navSections.map((section, sIdx) => (
                        <Box
                            key={section.title}
                            sx={{
                                mb: 0.5,
                                opacity: visible ? 1 : 0,
                                transform: visible ? "translateY(0)" : "translateY(12px)",
                                transition: `opacity 0.4s ease ${0.15 + sIdx * 0.08}s, transform 0.4s ease ${0.15 + sIdx * 0.08}s`,
                            }}
                        >
                            {/* Section divider */}
                            {!collapsed ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        px: 2,
                                        pt: sIdx > 0 ? 1.2 : 0.5,
                                        pb: 0.6,
                                        gap: 1.2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            height: "1px",
                                            background: darkMode
                                                ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)"
                                                : "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.08) 50%, transparent 100%)",
                                        }}
                                    />
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            fontSize: "0.6rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.1em",
                                            color: darkMode
                                                ? "rgba(255,255,255,0.55)"
                                                : "rgba(0,0,0,0.4)",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            height: "1px",
                                            background: darkMode
                                                ? "linear-gradient(90deg, rgba(255,255,255,0.18) 0%, transparent 100%)"
                                                : "linear-gradient(90deg, rgba(0,0,0,0.08) 0%, transparent 100%)",
                                        }}
                                    />
                                </Box>
                            ) : (
                                sIdx > 0 && (
                                    <Box
                                        sx={{
                                            mx: 1.5,
                                            my: 1,
                                            height: "1px",
                                            background: darkMode
                                                ? "rgba(255,255,255,0.12)"
                                                : "rgba(0,0,0,0.06)",
                                        }}
                                    />
                                )
                            )}

                            {/* Items */}
                            <List dense disablePadding>
                                {section.items.map((item) => {
                                    const IconComp = item.icon;
                                    const active = isItemActive(item);
                                    const accent = ACCENT[item.id] || "#fff";
                                    const grad = GRADIENT[item.id] || ["#fff", "#ccc"];
                                    const isVault = !!item.vaultSection;

                                    const content = (
                                        <ListItemButton
                                            disabled={item.comingSoon}
                                            selected={active}
                                            onClick={() => {
                                                if (item.id === "quiz") {
                                                    onOpenQuiz();
                                                } else {
                                                    onNavClick(
                                                        item.label,
                                                        item.content,
                                                        item.comingSoon,
                                                        item.vaultSection ?? null
                                                    )
                                                }
                                            }}
                                            sx={{
                                                borderRadius: collapsed ? 2 : 2.5,
                                                px: collapsed ? 1 : 1.5,
                                                py: 0.7,
                                                mx: collapsed ? 0.5 : 1,
                                                mb: 0.3,
                                                position: "relative",
                                                overflow: "hidden",
                                                color: darkMode ? "#C8C8CD" : "grey.700",
                                                transition:
                                                    "background-color 0.2s ease, transform 0.15s ease, color 0.2s ease",

                                                /* ── Hover ── */
                                                "&:hover": {
                                                    backgroundColor: darkMode
                                                        ? "rgba(255,255,255,0.06)"
                                                        : "rgba(0,0,0,0.04)",
                                                    transform: "scale(1.015)",
                                                    color: darkMode ? "#F0F0F5" : "grey.900",
                                                    "& .nav-icon": {
                                                        animation: "sidebarIconBounce 0.4s ease",
                                                        color: `${accent} !important`,
                                                    },
                                                    "& .shortcut-badge": {
                                                        opacity: 1,
                                                    },
                                                },

                                                /* ── Active ── */
                                                "&.Mui-selected": {
                                                    bgcolor: "transparent",
                                                    color: darkMode ? "#fff" : "#1D1D1F",
                                                    "&:hover": {
                                                        bgcolor: darkMode
                                                            ? "rgba(255,255,255,0.06)"
                                                            : "rgba(0,0,0,0.04)",
                                                    },
                                                },
                                            }}
                                        >
                                            {/* Active left accent bar */}
                                            {active && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        left: 0,
                                                        top: "15%",
                                                        bottom: "15%",
                                                        width: 3,
                                                        borderRadius: 2,
                                                        background: `linear-gradient(180deg, ${grad[0]}, ${grad[1]})`,
                                                        boxShadow: `0 0 8px ${grad[0]}40`,
                                                        transition: "all 0.3s ease",
                                                    }}
                                                />
                                            )}

                                            {/* Active background glow */}
                                            {active && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        background: `radial-gradient(ellipse at 0% 50%, ${accent}${darkMode ? "22" : "18"} 0%, transparent 70%)`,
                                                        pointerEvents: "none",
                                                    }}
                                                />
                                            )}

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    width: "100%",
                                                    gap: collapsed ? 0 : 1.2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: collapsed ? 0 : 1.2,
                                                        flexGrow: 1,
                                                        justifyContent: collapsed ? "center" : "flex-start",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: "relative",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <IconComp
                                                            fontSize="small"
                                                            className="nav-icon"
                                                            sx={{
                                                                flexShrink: 0,
                                                                color: active ? accent : "inherit",
                                                                transition: "color 0.25s ease",
                                                                fontSize: "1.2rem",
                                                            }}
                                                        />
                                                        {/* Collapsed active dot */}
                                                        {collapsed && active && (
                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    bottom: -6,
                                                                    left: "50%",
                                                                    transform: "translateX(-50%)",
                                                                    width: 4,
                                                                    height: 4,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: accent,
                                                                    boxShadow: `0 0 6px ${accent}`,
                                                                    animation: "sidebarGlowDot 2s ease infinite",
                                                                }}
                                                            />
                                                        )}
                                                    </Box>

                                                    {!collapsed && (
                                                        <>
                                                            <ListItemText
                                                                primary={item.label}
                                                                primaryTypographyProps={{
                                                                    variant: "body2",
                                                                    noWrap: true,
                                                                    sx: {
                                                                        fontWeight: active ? 600 : 400,
                                                                        fontSize: "0.82rem",
                                                                        letterSpacing: "0.01em",
                                                                    },
                                                                }}
                                                            />
                                                            {item.comingSoon && (
                                                                <Chip
                                                                    label="Soon"
                                                                    size="small"
                                                                    sx={{
                                                                        height: 18,
                                                                        fontSize: "0.6rem",
                                                                        fontWeight: 600,
                                                                        letterSpacing: "0.04em",
                                                                        backgroundColor: darkMode
                                                                            ? "rgba(255,255,255,0.1)"
                                                                            : "rgba(0,0,0,0.06)",
                                                                        color: darkMode
                                                                            ? "rgba(255,255,255,0.65)"
                                                                            : "rgba(0,0,0,0.4)",
                                                                        border: `1px solid ${darkMode
                                                                            ? "rgba(255,255,255,0.15)"
                                                                            : "rgba(0,0,0,0.06)"
                                                                            }`,
                                                                    }}
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </Box>

                                                {/* Right side: count + shortcut */}
                                                {!collapsed && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {isVault && typeof item.count === "number" && (
                                                            <Chip
                                                                label={item.count}
                                                                size="small"
                                                                sx={{
                                                                    height: 18,
                                                                    minWidth: 26,
                                                                    fontSize: "0.65rem",
                                                                    fontWeight: 600,
                                                                    backgroundColor: active
                                                                        ? `${accent}25`
                                                                        : darkMode
                                                                            ? "rgba(255,255,255,0.1)"
                                                                            : "rgba(0,0,0,0.05)",
                                                                    color: active
                                                                        ? accent
                                                                        : darkMode
                                                                            ? "rgba(255,255,255,0.75)"
                                                                            : "rgba(0,0,0,0.45)",
                                                                    border: active
                                                                        ? `1px solid ${accent}30`
                                                                        : "none",
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </ListItemButton>
                                    );

                                    return (
                                        <ListItem key={item.id} disablePadding>
                                            {collapsed ? (
                                                <Tooltip title={item.label} placement="right" arrow>
                                                    {active ? (
                                                        <GlassContainer
                                                            sx={{
                                                                borderRadius: 2,
                                                                mx: 0.5,
                                                                mb: 0.3,
                                                                width: "100%",
                                                                overflow: "hidden",
                                                            }}
                                                            isMetallic
                                                            isDark={darkMode}
                                                        >
                                                            {content}
                                                        </GlassContainer>
                                                    ) : (
                                                        content
                                                    )}
                                                </Tooltip>
                                            ) : active ? (
                                                <GlassContainer
                                                    sx={{
                                                        borderRadius: 2.5,
                                                        mx: 1,
                                                        mb: 0.3,
                                                        width: "100%",
                                                        overflow: "hidden",
                                                    }}
                                                    isMetallic
                                                    isDark={darkMode}
                                                >
                                                    {content}
                                                </GlassContainer>
                                            ) : (
                                                content
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    ))}
                </Box>

                {/* ═══════ ADD CLIENT CTA ═══════ */}
                <Box
                    sx={{
                        p: collapsed ? 1 : 1.5,
                        opacity: visible ? 1 : 0,
                        transform: visible ? "translateY(0)" : "translateY(12px)",
                        transition: "opacity 0.4s ease 0.4s, transform 0.4s ease 0.4s",
                    }}
                >
                    {collapsed ? (
                        /* Collapsed: minimal round button */
                        <Tooltip title="Add Client" placement="right" arrow>
                            <Box
                                onClick={() =>
                                    !(partnerLoading || !hasToken) && onAddClient()
                                }
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 40,
                                    height: 40,
                                    mx: "auto",
                                    borderRadius: "50%",
                                    cursor:
                                        partnerLoading || !hasToken ? "default" : "pointer",
                                    opacity: partnerLoading || !hasToken ? 0.4 : 1,
                                    background: darkMode
                                        ? "linear-gradient(135deg, rgba(79,195,247,0.15), rgba(79,195,247,0.05))"
                                        : "linear-gradient(135deg, rgba(79,195,247,0.2), rgba(79,195,247,0.08))",
                                    border: `1px solid ${darkMode
                                        ? "rgba(79,195,247,0.2)"
                                        : "rgba(79,195,247,0.25)"
                                        }`,
                                    transition: "all 0.25s ease",
                                    "&:hover": {
                                        transform:
                                            partnerLoading || !hasToken
                                                ? "none"
                                                : "scale(1.1)",
                                        boxShadow: "0 0 16px rgba(79,195,247,0.25)",
                                    },
                                }}
                            >
                                <AddRounded
                                    sx={{
                                        fontSize: 20,
                                        color: "#4FC3F7",
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    ) : (
                        /* Expanded: premium CTA card */
                        <Box
                            onClick={() =>
                                !(partnerLoading || !hasToken) && onAddClient()
                            }
                            sx={{
                                cursor:
                                    partnerLoading || !hasToken ? "default" : "pointer",
                                width: "100%",
                                borderRadius: 3.5,
                                p: 2,
                                position: "relative",
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.2,
                                background: darkMode
                                    ? "linear-gradient(145deg, #1a1d27 0%, #13151d 50%, #1a1d27 100%)"
                                    : "linear-gradient(145deg, #fafbff 0%, #f0f2f8 50%, #e8eaf0 100%)",
                                border: `1px solid ${darkMode
                                    ? "rgba(79,195,247,0.12)"
                                    : "rgba(79,195,247,0.15)"
                                    }`,
                                boxShadow: darkMode
                                    ? "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)"
                                    : "0 4px 20px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)",
                                transition:
                                    "transform 0.25s cubic-bezier(.4,0,.2,1), box-shadow 0.25s ease, border-color 0.25s ease",
                                ...(partnerLoading || !hasToken
                                    ? { opacity: 0.5 }
                                    : {
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            borderColor: darkMode
                                                ? "rgba(79,195,247,0.3)"
                                                : "rgba(79,195,247,0.35)",
                                            boxShadow: darkMode
                                                ? "0 12px 40px rgba(0,0,0,0.5), 0 0 20px rgba(79,195,247,0.08)"
                                                : "0 8px 28px rgba(0,0,0,0.1), 0 0 20px rgba(79,195,247,0.06)",
                                            "& .cta-shimmer": { opacity: 1 },
                                            "& .cta-avatars .MuiAvatar-root:nth-of-type(1)": {
                                                transform: "translateX(-4px) rotate(-5deg)",
                                            },
                                            "& .cta-avatars .MuiAvatar-root:nth-of-type(3)": {
                                                transform: "translateX(4px) rotate(5deg)",
                                            },
                                        },
                                    }),
                            }}
                        >
                            {/* Shimmer sweep */}
                            <Box
                                className="cta-shimmer"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    opacity: 0,
                                    background:
                                        "linear-gradient(105deg, transparent 30%, rgba(79,195,247,0.06) 45%, rgba(79,195,247,0.12) 50%, rgba(79,195,247,0.06) 55%, transparent 70%)",
                                    backgroundSize: "200% 100%",
                                    animation: "sidebarShimmer 2s ease infinite",
                                    pointerEvents: "none",
                                    transition: "opacity 0.3s ease",
                                }}
                            />

                            {/* Pulsing ring */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: -1,
                                    borderRadius: 3.5,
                                    border: `1px solid ${darkMode
                                        ? "rgba(79,195,247,0.15)"
                                        : "rgba(79,195,247,0.2)"
                                        }`,
                                    animation: "sidebarPulseRing 3s ease infinite",
                                    pointerEvents: "none",
                                }}
                            />

                            {/* Avatars */}
                            <Box
                                className="cta-avatars"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 0.3,
                                }}
                            >
                                {[
                                    { src: "/images/dietitian/womantrackfood.jpg", size: 28 },
                                    { src: "/images/hero/hero7.png", size: 36 },
                                    { src: "/images/hero/hero5.png", size: 28 },
                                ].map((av, i) => (
                                    <Avatar
                                        key={i}
                                        src={av.src}
                                        sx={{
                                            width: av.size,
                                            height: av.size,
                                            border: `2px solid ${darkMode ? "#1a1d27" : "#fafbff"
                                                }`,
                                            ml: i > 0 ? -1 : 0,
                                            zIndex: 3 - Math.abs(1 - i),
                                            transition: "transform 0.3s cubic-bezier(.4,0,.2,1)",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                        }}
                                    />
                                ))}
                            </Box>

                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                    letterSpacing: "0.03em",
                                    color: darkMode ? "#E8E8ED" : "#1D1D1F",
                                    textAlign: "center",
                                }}
                            >
                                Add a Client
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: "0.68rem",
                                    color: darkMode
                                        ? "rgba(255,255,255,0.6)"
                                        : "rgba(0,0,0,0.45)",
                                    mt: -0.5,
                                    textAlign: "center",
                                }}
                            >
                                Give Pro access for free
                            </Typography>
                        </Box>
                    )}
                </Box>
            </GlassContainer>
        </Box>
    );
};

export default PartnerSidebar;
